import type { NextRouter } from 'next/router';
// eslint-disable-next-line no-restricted-imports
import { useRouter as useNextRouter } from 'next/router';
import { route } from 'nextjs-routes';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import {
  clearNavigatingPath,
  getNavigatingPath,
  setNavigatingPath,
} from '@/utils/navigationControl';
import { buildResolvedPath, ensureLocale } from '@/utils/urlUtils';

import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { paths } from '@/lib/paths';
import { LocaleContext } from '@/providers/LocaleProvider';

type RouterWithLocale = NextRouter & {
  generateRoute: typeof route;
  locale: string;
  paths: typeof paths;
  prefetch: NextRouter['prefetch'];
  push: NextRouter['push'];
  query: Record<string, string | string[] | undefined>;
  replace: NextRouter['replace'];
  shouldNavigate: (targetHref?: string, isShallow?: boolean) => boolean;
};

export function useRouter(): RouterWithLocale {
  const nextRouter = useNextRouter();
  const locale =
    useContext(LocaleContext) ||
    nextRouter.locale ||
    NEXT_PUBLIC_DEFAULT_APP_LOCALE;

  /**
   * Determines if navigation should proceed and manages navigation state.
   *
   * @param targetHref - The target URL to navigate to
   * @param isShallow - Whether this is a shallow navigation (URL change without full page reload)
   * @returns boolean - Whether navigation should proceed
   *
   * For shallow navigation:
   * - Always returns true to allow navigation
   * - Sets navigatingPath temporarily but immediately clears it in the next tick
   *   since routeChangeComplete doesn't fire for shallow navigation
   *
   * For regular navigation:
   * - Prevents duplicate navigations to the same path
   * - Sets navigatingPath which will be cleared when routeChangeComplete fires
   */
  const shouldNavigate = useCallback(
    (targetHref?: string, isShallow?: boolean) => {
      // For shallow navigation, allow navigation but handle cleanup specially
      if (isShallow) {
        if (targetHref) {
          // For shallow navigation, set the path but schedule immediate cleanup
          // since routeChangeComplete won't fire
          setNavigatingPath(targetHref);
          setTimeout(() => {
            if (getNavigatingPath() === targetHref) {
              clearNavigatingPath();
            }
          }, 0);
        }
        return true;
      }

      // For normal navigation
      if (!targetHref) return getNavigatingPath() === null;
      if (getNavigatingPath() === targetHref) return false;

      setNavigatingPath(targetHref);
      return true;
    },
    [],
  );

  const push = useCallback<NextRouter['push']>(
    async (url, as, options) => {
      const targetHref = ensureLocale(url, locale);
      const resolvedPath =
        typeof targetHref === 'object'
          ? buildResolvedPath(targetHref.pathname ?? '', targetHref.query)
          : targetHref;

      if (!shouldNavigate(resolvedPath, options?.shallow))
        return Promise.resolve(false);

      try {
        return await nextRouter.push(targetHref, as, options);
      } catch {
        clearNavigatingPath();
        return false;
      }
    },
    [locale, nextRouter, shouldNavigate],
  );

  const replace = useCallback<NextRouter['replace']>(
    async (url, as, options) => {
      const targetHref = ensureLocale(url, locale);
      const resolvedPath =
        typeof targetHref === 'object'
          ? buildResolvedPath(targetHref.pathname ?? '', targetHref.query)
          : targetHref;

      if (!shouldNavigate(resolvedPath, options?.shallow))
        return Promise.resolve(false);

      try {
        return await nextRouter.replace(targetHref, as, options);
      } catch {
        clearNavigatingPath();
        return false;
      }
    },
    [locale, nextRouter, shouldNavigate],
  );

  const prefetch = useCallback<NextRouter['prefetch']>(
    (url, as, options) =>
      nextRouter.prefetch(ensureLocale(url, locale) as string, as, options),
    [locale, nextRouter],
  );

  useEffect(() => {
    const handleRouteChangeComplete = (url: string) => {
      if (getNavigatingPath() === url) {
        clearNavigatingPath();
      }
    };

    const handleRouteChangeError = () => {
      clearNavigatingPath();
    };

    nextRouter.events.on('routeChangeComplete', handleRouteChangeComplete);
    nextRouter.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      nextRouter.events.off('routeChangeComplete', handleRouteChangeComplete);
      nextRouter.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [nextRouter]);

  const queryWithoutLang = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(nextRouter.query).filter(([key]) => key !== 'lang'),
      ),
    [nextRouter.query],
  );

  return useMemo(
    () => ({
      generateRoute: route,
      paths,
      ...nextRouter,
      locale,
      query: queryWithoutLang,
      push,
      replace,
      prefetch,
      shouldNavigate,
    }),
    [
      nextRouter,
      locale,
      queryWithoutLang,
      push,
      replace,
      prefetch,
      shouldNavigate,
    ],
  );
}
